import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import {
  DEFAULT_CENTER_POINT,
  DEFAULT_ZOOM_LEVEL_DESKTOP,
  DEFAULT_ZOOM_LEVEL_MOBILE,
  EMarkerStatuses,
  PIN_MODE_LAYER_ID,
} from "./Map.constants";
import {
  MapHitTestResultResults,
  MapRefContainer,
  MapViewClickEvent,
  MapViewPointerMoveEvent,
} from "./Map.types";
import Basemap from "@arcgis/core/Basemap";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as watchUtils from "@arcgis/core/core/watchUtils";
import { Action, Dispatch } from "@reduxjs/toolkit";
import store, { AppDispatch } from "../../state-management/store";
import { LocationWidget } from "./widgets/LocationWidget";
import { ZoomService } from "./widgets/ZoomWidget";

import { SearchWidget } from "./widgets/SearchWidget";
import {
  setBaseMapInitialized,
  updateMarkerData,
} from "../../state-management/slices/global/global.slice";
import { FeatureLayerService } from "./FeatureLayer.service";
import { ApiService } from "../../api/api.service";
import { DispatchLogger } from "../../state-management/utils/dispatch-logger/DispatchLogger";
import MapUtils from "./Map.utils";
import Extent from "@arcgis/core/geometry/Extent";
import { isMobile } from "react-device-detect";
import { EGoogleEventTypes, ELogTypes } from "../../shared/enums/logging.enums";
import { IAppConfig } from "../../shared/types/config.types";
import { getGlobalState } from "../../shared/utils/testing.utils";
import PinModeMarker from "../../theme/assets/pin-mode-marker.svg";

import {
  consoleErrorStyle,
  consoleSuccessStyle,
} from "../../shared/constants/logging.constants";

export class BaseMapService {
  private static instance: BaseMapService;
  private streetlightsLayer!: FeatureLayer;
  private pinModeLayer!: FeatureLayer;
  private restrictedLayer!: FeatureLayer;
  private grandRapidsLayer!: FeatureLayer;
  private mapView!: MapView;
  private searchWidget!: SearchWidget;
  private locationWidget!: LocationWidget;
  private basemapGallery!: BasemapGallery;
  private featureLayerService!: FeatureLayerService;
  private zoomWidget!: ZoomService;
  private currentExtent!: any;
  private appConfig!: IAppConfig;
  private featureLayerID!: string;
  private restrictedLayerID!: string;
  private grandRapidsLayerID!: string;
  private map: Map;
  private dispatch: AppDispatch;
  private APIService: ApiService;
  private dispatchLogger: DispatchLogger;
  private sendDataToGTM: any;

  /**
   * Creates an instance of BaseMapService.
   * @param {AppDispatch} dispatch
   * @memberof BaseMapService
   */
  private constructor(dispatch: AppDispatch, sendDataToGTM: any) {
    this.dispatch = dispatch;
    this.sendDataToGTM = sendDataToGTM;
    this.dispatchLogger = DispatchLogger.getInstance(dispatch);
    this.APIService = ApiService.getInstance();
    this.map = this.buildMap();
  }

  /**
   * @private buildMap
   * @return {*}  {WebMap}
   * @memberof BaseMapService
   */
  private buildMap(): Map {
    return new Map({
      basemap: "gray-vector",
    });
  }

  /**
   * initializeMapView
   * @param {MapRefContainer} mapContainer
   * @param {IAppConfig} appConfig
   * @memberof BaseMapService
   */
  public initializeMapView(
    mapContainer: MapRefContainer,
    basemapGalleryContainer: MapRefContainer,
    appConfig: IAppConfig
  ): void {
    if (this.mapView) {
      // eslint-disable-next-line no-console
      console.warn("[WARNING] MAP VIEW ALREADY INITIALIZED: ", {
        ...this.mapView,
      });
      return;
    }

    this.appConfig = appConfig;
    this.featureLayerID = appConfig.esriStreetlightLayerId;
    this.grandRapidsLayerID = appConfig.esriGrandRapidsLayerId;
    this.restrictedLayerID = appConfig.esriRestrictedLayerId;

    this.mapView = new MapView({
      map: this.map,
      container: mapContainer.current,
      center: DEFAULT_CENTER_POINT,
      zoom: isMobile ? DEFAULT_ZOOM_LEVEL_MOBILE : DEFAULT_ZOOM_LEVEL_DESKTOP,
      constraints: {
        rotationEnabled: false,
        minZoom: isMobile
          ? DEFAULT_ZOOM_LEVEL_MOBILE
          : DEFAULT_ZOOM_LEVEL_DESKTOP,
        maxZoom: 20,
      },
      highlightOptions: {
        fillOpacity: 0,
        haloOpacity: 0,
      },
      ui: {
        components: ["attribution"],
      },
    });

    this.buildWidgets(basemapGalleryContainer);
    this.initializeFeatureLayerService();
    this.addHandlers();
  }

  /**
   * @private addHandlers
   * @memberof BaseMapService
   * @description Attaches handlers for mapView life cycle and interaction events
   */
  private addHandlers() {
    this.mapView
      .when(() => this.mapViewLoadComplete())
      .finally(() => {
        // eslint-disable-next-line no-console
        console.info("%cMapView Initialized", consoleSuccessStyle);
        this.dispatch(setBaseMapInitialized(true));
      })
      .catch((reason: any) => this.mapViewLoadErrorHandler(reason));

    this.mapView.on("click", (event) => this.mapViewClickHandler(event));
    this.mapView.on("pointer-move", (event) =>
      this.mapViewPointerMovedHandler(event)
    );

    watchUtils.whenTrue(this.mapView, "stationary", () =>
      this.mapViewMoveComplete()
    );
  }

  /**
   * @private mapViewLoadComplete
   * @memberof BaseMapService
   * @description Called when mapView is completely loaded. Only called once on initial load.
   */
  private mapViewLoadComplete = (): Promise<any> => {
    MapUtils.renderLocationButtonIcon();
    this.currentExtent = this.mapView.extent;
    this.featureLayerService.queryFeatureLayer(this.currentExtent);
    return Promise.resolve();
  };

  /**
   * @private mapViewMoveComplete
   * @memberof BaseMapService
   * @description Called when the map is stationary after any map movement has occured.
   * Gets the new extent of the view only when view is stationary.
   */
  private mapViewMoveComplete = (): void => {
    const currentState = store.getState();
    const { routing } = currentState.globalState.ui;

    if (routing.outageId && !routing.routeNavigationComplete) {
      return;
    }

    if (this.mapView.extent) {
      this.currentExtent = this.mapView.extent;
      this.processExtent();
    }
  };

  /**
   * @private mapViewLoadErrorHandler
   * @param {*} reason
   * @memberof BaseMapService
   * @description Called if there is a problem loading the mapView
   */
  private mapViewLoadErrorHandler = (reason: any): void => {
    // eslint-disable-next-line no-console
    console.error(
      `MapView Initialization Failed: ${reason}`,
      consoleErrorStyle
    );

    this.dispatchLogger.fire(null, reason, {
      message: "Map View Loading Failed",
      type: ELogTypes.ERROR,
      data: { reason },
    });

    this.sendDataToGTM({
      event: "eventTracking",
      category: "Outage",
      action: "Street Light",
      label: `Map Load Error: Reason: ${reason}`,
    });
  };

  /**
   * @private mapViewPointerMovedHandler
   * @param {MapViewPointerMoveEvent} event
   * @memberof BaseMapService
   * @description Called every time the user's pointer moves over the map. We then call a
   * hitTest method that allows us to detect features below the pointer and change the user's cursor
   */
  private mapViewPointerMovedHandler = (event: MapViewPointerMoveEvent) => {
    this.mapView.hitTest(event).then((response) => {
      const container = document.getElementById("MapContainer");
      const { ui } = getGlobalState();
      const pinModeEnabled = ui.pinMode.enabled;

      if (response.results.length) {
        const streetlightLayer = response.results.filter((feature) => {
          return feature.graphic.layer.id === this.featureLayerID;
        });
        const grandRapidsLayer = response.results.filter((feature) => {
          return feature.graphic.layer.id === this.grandRapidsLayerID;
        });
        const restrictedLayer = response.results.filter((feature) => {
          return feature.graphic.layer.id === this.restrictedLayerID;
        });

        if (container) {
          if (pinModeEnabled) {
            if (restrictedLayer.length) {
              container.style.cursor = "not-allowed";
            } else {
              container.style.cursor = `url(${PinModeMarker}) 12 16, pointer`;
            }
          } else {
            if (streetlightLayer.length || grandRapidsLayer.length) {
              container.style.cursor = "pointer";
            } else if (restrictedLayer.length) {
              container.style.cursor = "not-allowed";
            } else {
              container.style.cursor = "default";
            }
          }
        }
      } else {
        if (container) {
          container.style.cursor = "default";
        }
      }
    });
  };

  /**
   * @private mapViewClickHandler
   * @param {MapViewClickEvent} event
   * @memberof BaseMapService
   * @description Called when the map is clicked. We then call a hitTest method that allows
   * us to detect which streetlight was clicked
   */
  private mapViewClickHandler = (event: MapViewClickEvent): void => {
    const globalState = getGlobalState();
    const { pinMode } = globalState.ui;
    const { mapPoint } = event;

    this.mapView
      .hitTest(event, {
        include: [
          this.getStreetlightsLayer(),
          this.getGrandRapidsLayer(),
          this.getPinModeLayer(),
          this.getRestrictedLayer(),
        ],
      })
      .then(async (hitTest: any) => {
        // Get selected streetLight markers
        const streetlightMarkers = hitTest.results.filter(
          (result: MapHitTestResultResults) => {
            return result.graphic.layer.id === this.featureLayerID;
          }
        );

        const grandRapidsMarkers = hitTest.results.filter(
          (result: MapHitTestResultResults) => {
            return result.graphic.layer.id === this.grandRapidsLayerID;
          }
        );

        // Get the restricted layer
        const restrictedLayer = hitTest.results.filter(
          (result: MapHitTestResultResults) => {
            return result.graphic.layer.id === this.restrictedLayerID;
          }
        );

        // If we're in pin-mode, and an existing streetlight was clicked, stop event and do nothing.
        // Otherwise, drop a pin at the selected location.
        if (pinMode.enabled) {
          if (streetlightMarkers.length || restrictedLayer.length) {
            event.stopPropagation();
            return;
          }

          const droppedPin = hitTest.results.filter(
            (result: MapHitTestResultResults) => {
              return result.graphic.layer.id === PIN_MODE_LAYER_ID;
            }
          );

          if (!droppedPin.length) {
            this.featureLayerService.addMarker(mapPoint);
          }
        }

        if (streetlightMarkers.length || grandRapidsMarkers.length) {
          let data;
          if (streetlightMarkers.length) {
            data = streetlightMarkers;
          } else if (grandRapidsMarkers.length) {
            data = grandRapidsMarkers;
          } else {
            data = null;
          }

          if (data) {
            data.forEach((layer: any, index: number, array: any) => {
              if (index === 0) {
                const { attributes } = layer.graphic;
                const selectionGraphic = layer.graphic;

                if (grandRapidsMarkers.length) {
                  attributes.markerStatus = EMarkerStatuses.NON_CE;
                }

                const markerData = {
                  attributes,
                };

                selectionGraphic.symbol = MapUtils.getMarkerByStatus(
                  attributes.markerStatus,
                  true
                );

                const eventLabel =
                  attributes.markerStatus === EMarkerStatuses.REPORTED ||
                  attributes.markerStatus === EMarkerStatuses.IN_PROGRESS
                    ? `${EGoogleEventTypes.MAP_MARKER_CLICKED} Reported`
                    : `${EGoogleEventTypes.MAP_MARKER_CLICKED} Not-Reported`;

                this.sendDataToGTM({
                  event: "eventTracking",
                  category: "Outage",
                  action: "Street Light",
                  label: eventLabel,
                });

                this.removeMapViewGraphics();
                this.mapView.graphics.add(selectionGraphic);
                this.dispatch(updateMarkerData(markerData));
              }
            });
          }
        } else {
          this.removeMapViewGraphics();
        }
      });
  };

  /**
   * @private initializeFeatureLayerService
   * @memberof BaseMapService
   * @description Creates and initializes the FeatureLayerService
   */
  private initializeFeatureLayerService() {
    this.featureLayerService = FeatureLayerService.getInstance(
      this.map,
      this.mapView,
      this.dispatch,
      this.appConfig,
      this.locationWidget
    );
  }

  /**
   * @private buildWidgets
   * @memberof BaseMapService
   */
  private buildWidgets(basemapGalleryContainer: MapRefContainer): void {
    this.searchWidget = SearchWidget.getInstance(
      this.mapView,
      this.dispatch,
      this.sendDataToGTM
    );

    this.locationWidget = LocationWidget.getInstance(
      this.mapView,
      this.dispatch
    );

    this.zoomWidget = ZoomService.getInstance(this.mapView);

    this.basemapGallery = new BasemapGallery({
      view: this.mapView,
      container: basemapGalleryContainer.current,
      source: [
        Basemap.fromId("gray-vector"),
        Basemap.fromId("satellite"),
        Basemap.fromId("hybrid"),
        Basemap.fromId("topo-vector"),
        Basemap.fromId("streets-vector"),
        Basemap.fromId("streets-navigation-vector"),
      ],
      visible: false,
    });

    this.mapView.ui.add(this.basemapGallery);
  }

  /**
   * getMapView
   * @return {*}  {MapView}
   * @memberof BaseMapService
   */
  public getMapView(): MapView {
    return this.mapView;
  }

  /**
   * getMap
   * @return {*}  {WebMap}
   * @memberof BaseMapService
   */
  public getMap(): Map {
    return this.map;
  }

  public openBasemapGallery(): void {
    this.basemapGallery.visible = true;
  }

  public closeBasemapGallery(): void {
    this.basemapGallery.visible = false;
  }

  /**
   * getLocationWidget
   * @return {*}  {LocationWidget}
   * @memberof BaseMapService
   */
  public getLocationWidget(): LocationWidget {
    return this.locationWidget;
  }
  /**
   * getStreetlightsLayer
   * @return {*}  {FeatureLayer}
   * @memberof BaseMapService
   */
  public getStreetlightsLayer(): FeatureLayer {
    if (!this.streetlightsLayer) {
      this.streetlightsLayer = this.featureLayerService.getStreetlightsLayer();
    }
    return this.streetlightsLayer;
  }

  /**
   * getPinModeLayer
   * @return {*}  {FeatureLayer}
   * @memberof BaseMapService
   */
  public getPinModeLayer(): FeatureLayer {
    if (!this.pinModeLayer) {
      this.pinModeLayer = this.featureLayerService.getPinModeLayer();
    }

    return this.pinModeLayer;
  }

  /**
   * getRestrictedLayer
   * @return {*}  {FeatureLayer}
   * @memberof BaseMapService
   */
  public getRestrictedLayer(): FeatureLayer {
    if (!this.restrictedLayer) {
      this.restrictedLayer = this.featureLayerService.getRestrictedLayer();
    }

    return this.restrictedLayer;
  }

  public getGrandRapidsLayer(): FeatureLayer {
    if (!this.grandRapidsLayer) {
      this.grandRapidsLayer = this.featureLayerService.getGrandRapidsLayer();
    }

    return this.grandRapidsLayer;
  }

  /**
   * removeMapViewGraphics
   * @memberof BaseMapService
   */
  public removeMapViewGraphics = (): void => {
    this.mapView.graphics.removeAll();
  };

  public getCurrentExtent = (): Extent => {
    return this.currentExtent;
  };

  public getFeatureLayerService = (): FeatureLayerService => {
    return this.featureLayerService;
  };

  public processExtent = (): void => {
    MapUtils.processExtent(
      this.currentExtent,
      this.APIService,
      this.dispatchLogger,
      this.featureLayerService
    );
  };

  /**
   * @static getInstance
   * @param {Dispatch<Action<any>>} dispatch
   * @return {*}  {BaseMapService}
   * @memberof BaseMapService
   */
  public static getInstance(
    dispatch: Dispatch<Action<any>>,
    sendDataToGTM: any
  ): BaseMapService {
    if (!BaseMapService.instance) {
      BaseMapService.instance = new BaseMapService(dispatch, sendDataToGTM);
    }

    return BaseMapService.instance;
  }
}
